import { useQuery } from '@apollo/client/react'
import type { ErrorLike } from '@apollo/client'

import { graphql } from '~publish/graphql'
import type { ResultOf } from '~publish/graphql'

import { toUTCDayEnd, toUTCDayStart } from '../../hooks/toUTCDayBounds'

export const ChannelBenchmarksDocument = graphql(/* GraphQL */ `
  query ChannelBenchmarks($input: ChannelBenchmarksInput!) {
    channelBenchmarks(input: $input) {
      series {
        channel {
          id
        }
        type
        displayName
        description
        unit
        history {
          month
          channelValue
          channelPercentileRank
          comparison {
            sampleSize
            percentiles {
              p50
            }
          }
        }
      }
    }
  }
`)

export type ChannelBenchmarksResult = ResultOf<typeof ChannelBenchmarksDocument>
export type BenchmarkSeries =
  ChannelBenchmarksResult['channelBenchmarks']['series'][number]

interface UseChannelBenchmarksOptions {
  organizationId: string
  channelId: string | undefined
  startDate: Date
  endDate: Date
  skip?: boolean
}

interface UseChannelBenchmarksResult {
  series: BenchmarkSeries[]
  loading: boolean
  error: ErrorLike | undefined
}

/** Shared empty result so a skipped/empty query returns a *stable* reference: a
 *  fresh `[]` each render would change `series` identity every render, cascading
 *  into new chart series/data and restarting the chart's line entry animation. */
const EMPTY_BENCHMARK_SERIES: BenchmarkSeries[] = []

export function useChannelBenchmarks({
  organizationId,
  channelId,
  startDate,
  endDate,
  skip = false,
}: UseChannelBenchmarksOptions): UseChannelBenchmarksResult {
  const isSkipped = skip || !organizationId || !channelId

  const { data, previousData, loading, error } = useQuery(
    ChannelBenchmarksDocument,
    {
      variables: {
        input: {
          organizationId,
          channelIds: channelId ? [channelId] : [],
          startDate: toUTCDayStart(startDate),
          endDate: toUTCDayEnd(endDate),
          metrics: ['followerGrowth'],
        },
      },
      fetchPolicy: 'cache-first',
      skip: isSkipped,
    },
  )

  // A skipped query never refetches, so `previousData` would otherwise surface the
  // last-viewed channel's benchmark series indefinitely (e.g. LinkedIn data bleeding
  // onto a non-LinkedIn channel). Only fall back to previousData while actively loading.
  const effectiveData = isSkipped ? undefined : (data ?? previousData)

  return {
    series: effectiveData?.channelBenchmarks?.series ?? EMPTY_BENCHMARK_SERIES,
    loading,
    error,
  }
}
